/**
 * Where the reading of an uploaded document stands (contract v8).
 *
 * The API answers the upload as soon as the reading is **queued**: `email_id` set,
 * `offer_read_at` null. From then on two rows tell the story, and neither alone is
 * enough. The quote says whether a reading landed (`offer_read_at`); the email says
 * whether the worker is still on it (`pending`), gave up (`error`) or finished
 * (`processed`). A `processed` email next to a quote with `offer_read_at` null is the one
 * case that needs care: it is either the quote row not refetched yet, or a reading that
 * never happened (the daily budget ran out and the worker left the email classified).
 * `offerReadingStateOf` cannot tell those apart from one sample, so `useOfferReading`
 * allows the quote a few more polls before calling it `unread`.
 *
 * Pure and exported on its own so the table of cases is a unit test, not a screen test.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { emailDetailKey, sharedEmailsApi } from '@/shared/emails/api';
import { isRetrying } from '@/shared/emails/failureCodes';
import { MANUAL_MAILBOX, type EmailDetail } from '@/shared/emails/types';

import { quoteKeys, quotesApi } from './api';
import { estimatedReadingSeconds } from './offerFiles';
import type { Quote } from './types';

export type OfferReadingState =
  /** No document behind this quote. */
  | 'none'
  /** Queued or being read. Poll. */
  | 'reading'
  /** A reading landed, lines or a note. Done. */
  | 'read'
  /**
   * The provider could not answer and the platform is asking again on its own
   * (`auto_retry`, contract v10.31): a wait, not an error. Polled slowly.
   */
  | 'retrying'
  /** The provider failed and nothing retries it any more. Reprocesar retries. */
  | 'failed'
  /** The email settled without a reading (budget, or a race we gave up waiting on). */
  | 'unread';

/** How often the two rows are asked for while a reading is in flight. */
export const READING_POLL_MS = 2500;

/** How often while the platform retries on its own: its waits are minutes long. */
export const RETRY_POLL_MS = 30_000;

/**
 * How many refreshes of the quote to allow after the email says `processed` before
 * concluding nothing was read. The worker writes the quote and the email in one
 * transaction, so the first refresh after the email's change is enough in practice;
 * the extra ones cover a slow answer.
 */
export const SETTLE_GRACE_POLLS = 3;

export function offerReadingStateOf(
  quote: Pick<Quote, 'email_id' | 'offer_read_at'>,
  email: Pick<EmailDetail, 'status' | 'auto_retry' | 'reading_error_code'> | undefined,
  settledPolls = 0,
  emailUnavailable = false,
): OfferReadingState {
  if (!quote.email_id) return 'none';
  if (quote.offer_read_at) return 'read';
  // The email cannot be read (gone, or hidden from this role): nothing to wait for. Said
  // as `unread` rather than spinning for ever on a row that will never answer.
  if (emailUnavailable) return 'unread';
  if (email === undefined) return 'reading';
  if (email.status === 'pending') return 'reading';
  if (isRetrying(email.auto_retry)) return 'retrying';
  if (email.status === 'error') return 'failed';
  // Classified as an offer, the reading failed and nothing retries it any more: it has
  // a code (contract v10.31), which is more than «no llegó a leerse».
  if (email.reading_error_code) return 'failed';
  return settledPolls >= SETTLE_GRACE_POLLS ? 'unread' : 'reading';
}

export function isManualUpload(email: Pick<EmailDetail, 'mailbox'> | undefined): boolean {
  return email?.mailbox === MANUAL_MAILBOX;
}

/**
 * After this many times the estimate, the screen says so. Two, not one: the estimate is
 * a median and the provider has slow afternoons; saying "late" at 1.1× would cry wolf on
 * a reading that lands ten seconds later.
 */
export const LATE_FACTOR = 2;
/** And never before this, whatever the estimate: a PDF can take 30 s on a bad day. */
export const LATE_FLOOR_SECONDS = 90;

/**
 * What the person is told while the reading is in flight: the estimate for these files,
 * how long it has been, and whether it is taking longer than it should.
 *
 * `startedAt` is the email's `received_at`, which for an upload is the moment it was
 * queued. Pure, so the table of cases is a unit test.
 */
export function readingProgress(
  email: Pick<EmailDetail, 'received_at' | 'attachments'> | undefined,
  now: number,
): { elapsedSeconds: number; estimateSeconds: number; late: boolean } {
  const names = email?.attachments?.map((attachment) => attachment.filename) ?? [];
  const estimateSeconds = estimatedReadingSeconds(names);
  const started = email ? Date.parse(email.received_at) : Number.NaN;
  const elapsedSeconds = Number.isFinite(started) ? Math.max(0, (now - started) / 1000) : 0;
  const late = elapsedSeconds > Math.max(LATE_FLOOR_SECONDS, estimateSeconds * LATE_FACTOR);
  return { elapsedSeconds: Math.floor(elapsedSeconds), estimateSeconds, late };
}

/**
 * The state above, kept current while it matters.
 *
 * While the reading is in flight both rows are polled every `READING_POLL_MS`: the
 * email, because its status is what changes first, and the quote, because its
 * `offer_read_at` and its lines are what the person is waiting for. A second observer
 * of `quoteKeys.detail(id)` with an interval is how React Query shares the cache with
 * the page's own `useQuote`: the smallest interval among observers wins, and when this
 * one goes to `false` the page is back to fetching on demand. Nothing is polled once the
 * reading is `read`, `failed`, `unread` or `none`; while it is `retrying`, every
 * `RETRY_POLL_MS`.
 */
export function useOfferReading(quote: Quote): {
  state: OfferReadingState;
  email: EmailDetail | undefined;
  manual: boolean;
} {
  const emailId = quote.email_id ?? '';
  const email = useQuery({
    queryKey: emailDetailKey(emailId),
    queryFn: () => sharedEmailsApi.detail(emailId),
    enabled: Boolean(emailId),
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      const status = query.state.data?.status;
      if (quote.offer_read_at) return false;
      if (status === undefined || status === 'pending') return READING_POLL_MS;
      // Retried by the platform: the lines will land on their own, minutes from now.
      return isRetrying(query.state.data?.auto_retry) ? RETRY_POLL_MS : false;
    },
  });
  const emailStatus = email.data?.status;

  // Refreshes of the quote seen since the email left `pending`. A ref and not state: it
  // only feeds the next state computation, which the refresh itself re-renders.
  const settledPolls = useRef(0);
  const waiting =
    offerReadingStateOf(quote, email.data, settledPolls.current, email.isError) === 'reading';
  const detail = useQuery({
    queryKey: quoteKeys.detail(quote.id),
    queryFn: () => quotesApi.detail(quote.id),
    enabled: waiting,
    refetchInterval: waiting ? READING_POLL_MS : false,
  });
  useEffect(() => {
    if (emailStatus === undefined || emailStatus === 'pending') {
      settledPolls.current = 0;
      return;
    }
    if (!quote.offer_read_at) settledPolls.current += 1;
  }, [detail.dataUpdatedAt, emailStatus, quote.offer_read_at]);

  return {
    state: offerReadingStateOf(quote, email.data, settledPolls.current, email.isError),
    email: email.data,
    manual: isManualUpload(email.data),
  };
}
